"use client";
import { useMemo, useState } from "react";

import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import AnnotatedImageView from "@demo/features/media/AnnotatedImageView";
import { RAIL_SIZE } from "@demo/features/media/FilmstripRail";
import { useHasRunOutput } from "@demo/hooks/useHasRunOutput";
import { useIsMobile } from "@demo/hooks/useIsMobile";
import { useDisplayedMediaIndex, useMediaCarouselStore } from "@demo/store/media-carousel";
import { useModelStore } from "@demo/store/model";

import { LAYOUT_BREAKPOINT } from "./constants";
import { OutputFilmstrip } from "./OutputFilmstrip";

/**
 * Compose's image output pane: the shared {@link AnnotatedImageView} scaffold
 * wrapped in Compose-only chrome — an agent-content-driven height (collapsed
 * until there's output) and a mobile fullscreen toggle.
 */
const ImageOutput = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const hasRunOutput = useHasRunOutput();
  const isEmptyResponse = useModelStore((state) => state.isEmptyResponse);

  const hasContent = hasRunOutput || isEmptyResponse;

  const media = useMediaCarouselStore((state) => state.mediaInAgentCarousel);
  const currentMediaIndex = useDisplayedMediaIndex();
  const currentMedia = useMemo(() => media[currentMediaIndex], [media, currentMediaIndex]);

  // The rail overlays the panel edge, so the media lays out past it. A single
  // asset has no rail, and the viewer takes the whole panel.
  const isMobile = useIsMobile(LAYOUT_BREAKPOINT);
  const showRail = media.length > 1;

  return (
    <div
      className={cn(
        "transition-[height, opacity] z-50 flex items-center justify-center overflow-hidden rounded-2xl bg-black transition-[height] duration-500 ease-in-out sm:duration-300",
        {
          "h-[0%]": !hasContent,
          "h-full": isFullscreen,
          "h-1/2 sm:h-full": !isFullscreen,
        },
      )}
      data-testid="media-carousel"
    >
      <div className="relative flex h-full w-full items-center justify-center">
        {currentMedia && (
          // Keyed by asset so switching remounts and replays the fade-in.
          <div
            className="asset-enter h-full w-full"
            key={currentMedia.key}
            style={{
              paddingLeft: showRail && !isMobile ? RAIL_SIZE : 0,
              paddingTop: showRail && isMobile ? RAIL_SIZE : 0,
            }}
          >
            <AnnotatedImageView media={currentMedia} />
          </div>
        )}
        <OutputFilmstrip orientation={isMobile ? "horizontal" : "vertical"} />

        <div className="from-white/6 absolute bottom-2 right-2 rounded-[8px] border border-solid border-transparent bg-foreground bg-gradient-to-br to-black/20 bg-origin-border p-2 dark:bg-background sm:hidden">
          <Icon
            className="size-5 text-white"
            name={isFullscreen ? "minimize" : "maximize"}
            onClick={() => setIsFullscreen(!isFullscreen)}
          />
        </div>
      </div>
    </div>
  );
};

export default ImageOutput;
